import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getTheaters,
  getCities,
  updateTheater,
  deleteTheater,
  type Theater,
  type City,
} from '../../api/admin';

interface TheaterManagementDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onTheaterUpdated: () => void;
}

export default function TheaterManagementDrawer({
  isOpen,
  onClose,
  onTheaterUpdated,
}: TheaterManagementDrawerProps) {
  const [theaters, setTheaters] = useState<Theater[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [editingTheaterId, setEditingTheaterId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editZip, setEditZip] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<Theater | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const previousOverflow = useRef<string>('');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const [theatersData, citiesData] = await Promise.all([
        getTheaters(),
        getCities(),
      ]);
      setTheaters(theatersData);
      setCities(citiesData);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load theaters');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement;
      previousOverflow.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      loadData();
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = previousOverflow.current;
      setSearchQuery('');
      setEditingTheaterId(null);
      setSaveError(null);
      setDeleteTarget(null);
      setDeleteError(null);
      triggerRef.current?.focus();
    }

    return () => {
      document.body.style.overflow = previousOverflow.current;
    };
  }, [isOpen, loadData]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (deleteTarget) {
          e.stopPropagation();
          setDeleteTarget(null);
          setDeleteError(null);
        } else if (editingTheaterId) {
          e.stopPropagation();
          setEditingTheaterId(null);
          setSaveError(null);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, editingTheaterId, deleteTarget, onClose]);

  const citiesMap = cities.reduce<Record<string, City>>((acc, city) => {
    acc[city.id] = city;
    return acc;
  }, {});

  const filteredTheaters = theaters.filter((theater) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const city = theater.city_id ? citiesMap[theater.city_id] : null;
    const cityName = city ? `${city.name} ${city.state}`.toLowerCase() : '';
    return (
      theater.name.toLowerCase().includes(query) ||
      cityName.includes(query)
    );
  });

  const startEdit = (theater: Theater) => {
    setEditingTheaterId(theater.id);
    setEditName(theater.name);
    setEditAddress(theater.address || '');
    setEditZip(theater.zip_code || '');
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditingTheaterId(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editingTheaterId || !editName.trim()) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateTheater(editingTheaterId, {
        name: editName.trim(),
        address: editAddress.trim() || undefined,
        zip_code: editZip.trim() || undefined,
      });
      setEditingTheaterId(null);
      await loadData();
      onTheaterUpdated();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save theater');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = (theater: Theater) => {
    setDeleteTarget(theater);
    setDeleteError(null);
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteTheater(deleteTarget.id);
      setDeleteTarget(null);
      await loadData();
      onTheaterUpdated();
    } catch (err) {
      // Keep dialog open so the user can read the conflict message
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete theater');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 ${isOpen ? '' : 'pointer-events-none'}`}
    >
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={deleteTarget ? undefined : onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-warm-200">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-warm-900">Manage Theaters</h2>
            {!isLoading && !fetchError && (
              <span className="text-xs bg-warm-100 text-warm-600 px-2 py-0.5 rounded-full">
                {filteredTheaters.length}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-warm-400 hover:text-warm-600 rounded transition-colors"
            aria-label="Close drawer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-warm-200 sticky top-0 bg-white">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search theaters..."
            className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          )}

          {fetchError && (
            <div className="p-4 text-center">
              <p className="text-sm text-danger-600 mb-3">{fetchError}</p>
              <button
                type="button"
                onClick={loadData}
                className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !fetchError && filteredTheaters.length === 0 && (
            <div className="p-4 text-center">
              <p className="text-sm text-warm-500">No theaters found</p>
            </div>
          )}

          {!isLoading && !fetchError && filteredTheaters.map((theater) => {
            const city = theater.city_id ? citiesMap[theater.city_id] : null;
            const isEditing = editingTheaterId === theater.id;

            return (
              <div key={theater.id} className="border-b border-warm-100">
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-warm-900 truncate">{theater.name}</p>
                    {city && (
                      <p className="text-xs text-warm-400 mt-0.5">
                        {city.name}, {city.state}
                      </p>
                    )}
                    {theater.address && (
                      <p className="text-sm text-warm-500 truncate mt-0.5">{theater.address}</p>
                    )}
                  </div>
                  {!isEditing && (
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(theater)}
                        className="p-1.5 text-warm-400 hover:text-primary-600 rounded transition-colors"
                        aria-label={`Edit ${theater.name}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => confirmDelete(theater)}
                        className="p-1.5 text-warm-400 hover:text-danger-600 rounded transition-colors"
                        aria-label={`Delete ${theater.name}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                {isEditing && (
                  <div className="px-4 pb-4 space-y-3 bg-warm-50">
                    <div>
                      <label className="block text-xs font-medium text-warm-600 mb-1">Name *</label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => { setEditName(e.target.value); setSaveError(null); }}
                        className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-warm-600 mb-1">Address</label>
                      <input
                        type="text"
                        value={editAddress}
                        onChange={(e) => { setEditAddress(e.target.value); setSaveError(null); }}
                        className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        placeholder="Street address"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-warm-600 mb-1">ZIP Code</label>
                      <input
                        type="text"
                        value={editZip}
                        onChange={(e) => { setEditZip(e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').slice(0, 10)); setSaveError(null); }}
                        className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        placeholder="12345 or L8P 4S8"
                      />
                    </div>
                    {city && (
                      <div>
                        <label className="block text-xs font-medium text-warm-600 mb-1">City (read-only)</label>
                        <p className="text-sm text-warm-500 px-4 py-2.5 bg-warm-100 rounded-lg">
                          {city.name}, {city.state}
                        </p>
                      </div>
                    )}

                    {saveError && (
                      <p className="text-xs text-danger-600">{saveError}</p>
                    )}

                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        disabled={isSaving}
                        className="px-4 py-2.5 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors text-sm font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving || !editName.trim()}
                        className="px-4 py-2.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Delete confirm dialog */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-theater-title"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 id="delete-theater-title" className="text-base font-semibold text-warm-900 mb-2">
              Delete theater?
            </h3>
            <p className="text-sm text-warm-600 mb-4">
              <span className="font-medium">{deleteTarget.name}</span> will be permanently removed.
              This cannot be undone.
            </p>

            {deleteError && (
              <p className="text-xs text-danger-600 bg-danger-50 border border-danger-200 rounded-lg px-3 py-2 mb-4">
                {deleteError}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelDelete}
                disabled={isDeleting}
                className="px-4 py-2.5 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2.5 bg-danger-600 text-white text-sm rounded-lg hover:bg-danger-700 disabled:opacity-50 transition-colors font-medium"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
